import { postData, getData, deleteData, patchData } from 'src/utils/axios';
import { urlMap } from 'src/utils/urls';
import cacheManager from 'src/utils/cache';

export const getPackages = async (clinicId) => {
  const url = `${urlMap.packages.list.replace(':clinicId', clinicId)}`;
  const result = await getData(url);
  
  // Cache nested treatment and concern data
  if (result && Array.isArray(result)) {
    result.forEach((pkg) => {
      if (pkg.treatment) {
        const treatmentCacheKey = cacheManager.generateCacheKey(`/v1/treatments/${pkg.treatment.id || pkg.treatment_id}`);
        cacheManager.set(treatmentCacheKey, pkg.treatment);
      }
      if (pkg.concerns && Array.isArray(pkg.concerns)) {
        pkg.concerns.forEach((concern) => {
          const concernCacheKey = cacheManager.generateCacheKey(`/v1/concerns/${concern.id}`);
          cacheManager.set(concernCacheKey, concern);
        });
      }
    });
  }
  
  return result;
};

export const addPackage = async (packageData) => {
  const url = `${urlMap.packages.create}`;
  const result = await postData(url, packageData);
  
  // Invalidate package lists and clinic dashboard
  cacheManager.invalidateResource('packages');
  if (packageData.clinic_id) {
    cacheManager.invalidate(`/v1/clinics/dashboard/${packageData.clinic_id}`);
    cacheManager.invalidate(`/v1/packages/clinic/${packageData.clinic_id}`);
  }
  
  return result;
};


export const deletePackage = async (packageId) => {
  const url = `${urlMap.packages.delete.replace(":packageId", packageId)}`;
  const result = await deleteData(url);
  
  // Invalidate package cache and clinic dashboard
  cacheManager.invalidateResource('packages', packageId);
  cacheManager.invalidate('/v1/clinics/dashboard');
  
  return result;
};


export const getPackageDetails = async (packageId) => {
  const url = `${urlMap.packages.getDetails.replace(":packageId", packageId)}`;
  const result = await getData(url);
  
  // Cache nested treatment and concern data
  if (result) {
    if (result.treatment) {
      const treatmentCacheKey = cacheManager.generateCacheKey(`/v1/treatments/${result.treatment.id || result.treatment_id}`);
      cacheManager.set(treatmentCacheKey, result.treatment);
    }
    if (result.treatment_id && Array.isArray(result.treatment_id)) {
      result.treatment_id.forEach((treatment) => {
        const treatmentCacheKey = cacheManager.generateCacheKey(`/v1/treatments/${treatment.id}`);
        cacheManager.set(treatmentCacheKey, treatment);
      });
    }
    if (result.concerns && Array.isArray(result.concerns)) {
      result.concerns.forEach((concern) => {
        const concernCacheKey = cacheManager.generateCacheKey(`/v1/concerns/${concern.id}`);
        cacheManager.set(concernCacheKey, concern);
      });
    }
  }
  
  return result;
};

export const updatePackage = async (packageId, packageData) => {
  const url = `${urlMap.packages.update.replace(":packageId", packageId)}`;
  const result = await patchData(url, packageData);
  
  // Explicitly invalidate package cache and related resources
  cacheManager.invalidateResource('packages', packageId);
  
  // Invalidate clinic dashboard if package has clinic_id
  if (packageData.clinic_id) {
    cacheManager.invalidate(`/v1/clinics/dashboard/${packageData.clinic_id}`);
  }
  
  // Invalidate nested treatment/concern caches if they were updated
  if (packageData.treatment_id) {
    cacheManager.invalidateResource('treatments');
  }
  if (packageData.concerns) {
    cacheManager.invalidateResource('concerns');
  }
  
  return result;
};


export const publishPackage = async (packageId, packageData) => {
  const url = `${urlMap.packages.publish.replace(":packageId", packageId)}`;
  const result = await patchData(url, packageData);
  
  // Invalidate package cache and clinic dashboard
  cacheManager.invalidateResource('packages', packageId);
  cacheManager.invalidate('/v1/clinics/dashboard');
  
  return result;
};
